import { useCallback, useState } from "react";

/**
 * useGeolocation — thin wrapper around the browser Geolocation API.
 * Returns `{ loading, coords, error, request, reset }`.
 *
 * `coords` is `{ lat, lng, accuracy }` when GPS locks; `null` otherwise.
 * `error` is a human-friendly message string; `""` when clean.
 *
 * Handles: insecure origins, unsupported browsers, permission denied,
 * position unavailable, and timeout — all mapped to friendly copy.
 */
export function useGeolocation({ timeout = 15000 } = {}) {
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setLoading(false);
    setCoords(null);
    setError("");
    // Only stable setState setters are used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const request = useCallback(() => {
    setLoading(true);
    setCoords(null);
    setError("");

    if (typeof window !== "undefined" && !window.isSecureContext) {
      setLoading(false);
      setError("GPS requires a secure (HTTPS) page. Type your address below instead — it works everywhere.");
      return;
    }
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setLoading(false);
      setError("Your browser doesn't support GPS. Type your address below.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLoading(false);
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        setLoading(false);
        let msg = "Couldn't read GPS. Type your address below instead.";
        if (err?.code === 1)
          msg = "GPS permission is blocked. Turn it on in your browser settings, or just type your address below — it works too.";
        else if (err?.code === 2) msg = "GPS unavailable right now. Type your address below instead.";
        else if (err?.code === 3) msg = "GPS timed out. Type your address below instead.";
        setError(msg);
      },
      { enableHighAccuracy: true, timeout, maximumAge: 0 }
    );
    // `timeout` is the only reactive value; setters and `navigator`/`window`
    // globals are stable. Scanner over-reports here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeout]);

  return { loading, coords, error, request, reset };
}
